/**
 * MontillaAds render contracts — the browser-side mirror of the `/ads`
 * decision response (RFC-MONTILLA-ADS v0.2.0 §11/§17; canonical copy lives in
 * the montilla repo at `lib/types/src/ads.ts`).
 *
 * The server OWNS every id here (decisionId, offerId, position, mad); the
 * browser only echoes them on `ad_impression`/`ad_click` events — that is what
 * joins impression ↔ click ↔ conversion of one decision into a funnel.
 */

export type AdFormat = 'carousel' | 'grid' | 'banner'
/** IAB size unit (+ `dynamic` = sem medida fixa; era `fluid`). */
export type StandardAdSize =
  | 'dynamic' | 'medium-rectangle' | 'large-rectangle' | 'leaderboard' | 'billboard'
  | 'half-page' | 'skyscraper' | 'wide-skyscraper' | 'mobile-banner'
  | 'large-mobile-banner' | 'portrait'

/**
 * A size that isn't in the IAB table, written into the string itself:
 * `custom:970x150`. Most placements in a real publication have no standard
 * name, and the alternative was to store them as `dynamic` — which reserves no
 * box at all and hands back the layout shift the size exists to prevent.
 *
 * It travels the same path as any other size (slot row → `/ads` →
 * `data-montilla-size` → here), so only `adSizeDimensions` reads the two forms.
 */
export type CustomAdSize = `custom:${number}x${number}`

export type AdSize = StandardAdSize | CustomAdSize

/**
 * IAB intrinsic dimensions per size (browser mirror of the contract's
 * `AD_SIZE_DIMENSIONS`; canonical copy in `lib/types/src/ads.ts`). `dynamic` is
 * `null` — responsive/native, no reserved box. Drives the CLS reservation: the
 * SDK sets `aspect-ratio` from this BEFORE the `/ads` round-trip, so a declared
 * slot never shifts layout when its fill arrives (see `reserveBox`).
 */
/**
 * O tamanho é "sem medida fixa"? Aceita os DOIS nomes, e isso é deliberado.
 *
 * O valor se chamava `fluid` e virou `dynamic`. O SDK não controla a versão do
 * servidor com que fala: uma página com o bundle novo pode receber `fluid` de um
 * rum-api que ainda não subiu, ou de uma resposta em cache de borda. Tratar só o
 * nome novo faria essa página tentar reservar caixa para um tamanho desconhecido.
 *
 * Na prática o `reserveBox` já degradava bem — tamanho desconhecido cai no mesmo
 * caminho responsivo, porque `adSizeDimensions` devolve `null`. Isto é o que torna
 * a intenção explícita em vez de acidental, e o que evita escrever
 * `data-montilla-size="fluid"` num host que não deveria ter atributo de medida.
 *
 * A recíproca não existe do lado do servidor: lá `fluid` só é aceito na ENTRADA da
 * escrita e gravado como `dynamic` (ver `normalizeAdSize` em `lib/types`).
 */
export function isDynamicSize(size: string | null | undefined): boolean {
  return size === 'dynamic' || size === 'fluid'
}

export const AD_SIZE_DIMENSIONS: Record<StandardAdSize, { width: number, height: number } | null> = {
  'dynamic': null,
  'medium-rectangle': { width: 300, height: 250 },
  'large-rectangle': { width: 336, height: 280 },
  'leaderboard': { width: 728, height: 90 },
  'billboard': { width: 970, height: 250 },
  'half-page': { width: 300, height: 600 },
  'skyscraper': { width: 160, height: 600 },
  'wide-skyscraper': { width: 300, height: 600 },
  'mobile-banner': { width: 320, height: 50 },
  'large-mobile-banner': { width: 320, height: 100 },
  'portrait': { width: 300, height: 1050 },
}

/** `custom:970x150` — 1 to 9999 px per side, no leading zero (one spelling per box). */
const CUSTOM_AD_SIZE_RE = /^custom:([1-9][0-9]{0,3})x([1-9][0-9]{0,3})$/

/**
 * Dimensions for ANY size — IAB name or written measure.
 *
 * The single place that reads both forms. Indexing `AD_SIZE_DIMENSIONS`
 * directly leaves a custom size `undefined`, and a slot then reserves no box in
 * silence: the page shifts again with nothing to say why.
 */
export function adSizeDimensions(size: string): { width: number, height: number } | null {
  const custom = CUSTOM_AD_SIZE_RE.exec(size)
  if (custom) return { width: Number(custom[1]), height: Number(custom[2]) }
  return AD_SIZE_DIMENSIONS[size as StandardAdSize] ?? null
}
/**
 * Viewport width (px) at/below which a slot counts as `mobile`, when the client
 * config doesn't override it via `ads.mobileBreakpoint`.
 *
 * ONE definition on purpose: this number decides three things that must agree —
 * which box is reserved (`data-montilla-size-mobile`), which device is sent to
 * `/ads` (so which creative is selected), and which art a `<picture>` serves.
 * A copy per call site is how a slot ends up with a mobile-sized box holding
 * desktop art.
 */
export const DEFAULT_MOBILE_BREAKPOINT = 768

/** How the SDK renders a slot's fill (render | swap | custom). */
export type RenderMode = 'render' | 'swap' | 'custom'

/**
 * Where an injected slot goes, relative to its host anchor element (config-
 * driven placement): `before`/`after` = sibling, `prepend`/`append` = first/
 * last child, `replace` = swap the anchor itself.
 */
export type AdMountPosition = 'before' | 'after' | 'prepend' | 'append' | 'replace'

/** A slot's config-driven placement: CSS selector or XPath + relative position. */
export interface AdMount {
  target: string
  position: AdMountPosition
}
/**
 * Como cada oferta é desenhada.
 *
 * `card` é a oferta de CATÁLOGO — ela não vem de uma peça cadastrada, vem do
 * acervo, e o SDK a desenha com o card de curso dele (logo, nome, nota, preço,
 * selos). Chamava-se `native` enquanto existia um tipo de peça com esse nome no
 * admin; o tipo saiu de lá (card montado por campos é o que `component` faz
 * melhor) e o nome passou a dizer o que a coisa é.
 *
 * Um bundle antigo recebendo `card` continua desenhando o mesmo card: ele é o
 * ramo PADRÃO do switch de render, não um caso nomeado.
 */
export type CreativeType = 'card' | 'text' | 'html' | 'image' | 'link' | 'component'

/**
 * One art of an `image` creative, with the narrowest viewport it is meant for.
 *
 * The art — not the SDK config — owns its condition, which is what `<picture>`
 * models natively: a creative can ship as many crops as it has, and the browser
 * picks. Nothing here depends on `ads.mobileBreakpoint`.
 *
 * By convention the list carries a `minWidth: 0` art — the fallback that renders
 * on the `<img>` itself, so it is what a browser without `<picture>` support
 * gets. It isn't enforced: the renderer always puts the NARROWEST art on the
 * `<img>`, so a list that forgets `0` still shows something.
 */
export interface AdImageArt {
  /** Narrowest viewport width (px) this art serves. `0` = fallback. */
  minWidth: number
  url: string
  /**
   * The FILE's measure, read when it was uploaded. Rendered as `width`/`height`
   * on the element that shows this art, so the browser reserves the box before
   * the bytes arrive — no layout shift. Only used with both.
   */
  width?: number
  height?: number
  /**
   * Resolution variants of THIS art (`… 480w, … 1080w`), generated by the server
   * up to the file width. Absent on older servers, and on art the server cannot
   * resize (third-party host, `data:`).
   */
  srcset?: string
  /**
   * Presentation OVERRIDES for this art. Absent = whatever the piece declared
   * (`image.fit` / `image.maxWidth` / `image.radius`), which is the common case.
   *
   * They live per art because the art and how it sits in the box travel
   * together: an edge-to-edge phone crop wants `radius: 0`, and the desktop
   * strip of the same piece may want it inset and capped. Same reasoning that
   * put the CROP per breakpoint — one value for the whole piece is a guess about
   * the other breakpoints.
   *
   * A list where nobody overrides costs nothing: the renderer keeps the inline
   * path and emits no extra CSS.
   */
  fit?: 'cover' | 'contain' | 'scale-down' | 'none' | 'fill'
  maxWidth?: number
  radius?: number
  /**
   * The description for THIS art. Absent = the piece's `image.alt`.
   *
   * It exists because a crop can carry different TEXT: the desktop strip shows
   * a price that does not fit the phone portrait, and one alt for the whole
   * piece would describe one of them wrong. When the crops say the same thing —
   * the common case — nobody fills it in and the piece answers for all.
   */
  alt?: string
  /**
   * The MODAL BOX to open while this art is the one on screen.
   *
   * One box for the whole piece is a wrong number the moment the crops differ in
   * shape: a 640x800 portrait wants a narrow box and the 1200x675 strip of the
   * SAME piece wants a wide one.
   *
   * Unlike everything else in the cascade this cannot be resolved on the server:
   * which art is on screen depends on the viewport. It rides along with the art
   * and the SDK applies it by media query, at the same widths that already pick
   * the `<source>`.
   *
   * `boxMode: 'full'` liga o TAKEOVER só nesta faixa: a tela inteira a partir de
   * uma largura, com a caixa medida nas outras. Isto contraria o que esta nota
   * dizia antes ("fullscreen is a MODE, not a value a media query can compose") —
   * o obstáculo não era o CSS, era a forma como a apresentação estava escrita:
   * numa classe. Extraída para `regrasDeTelaCheia`, ela sai também dentro de
   * media query, e cada faixa declara o conjunto INTEIRO — mesmo critério que a
   * apresentação das artes já usava, porque faixa que omite propriedade herda a
   * da faixa anterior, que é a caixa de outro recorte.
   *
   * `'full'` é o único modo que chega aqui: os outros dois viram número no
   * servidor (`px` é o próprio `maxWidth`, e `asset` é resolvido com a medida do
   * arquivo, que o payload não carrega). Chegando modo e largura juntos, o modo
   * ganha — é ele que descreve a apresentação inteira.
   */
  popup?: { maxWidth?: number, boxMode?: 'px' | 'asset' | 'full' }
}

/** A presentation height change at a minimum viewport width. */
export interface AdImageHeightRule {
  from: number
  height: number | string
}

/** Per-offer tracking stamp — the asset identity for the whole funnel. */
export interface AdOfferTracking {
  /** THE asset id (Demeter offer id or manual creative id). */
  offerId: string
  /**
   * Campanha DE VERDADE a que este item pertence — a dele quando tem vínculo
   * próprio (carrossel com peças de donos diferentes), senão a herdada do ad
   * set. Opcional: servidor antigo não manda.
   *
   * CUIDADO: `AdDecision.campaignId` carrega o id do AD SET, não de campanha.
   * Este é o único campo que carrega campanha de fato.
   */
  campaignId?: string
  /** The Ad id (reporting grain) when the offer came from an ad; null else. */
  adId: string | null
  /** Rank in the slate — CTR varies a lot by position. */
  position: number
  /**
   * Pre-built mad link id (`mad.<app>.<campaign>.<offerId>.<nonce>`). The
   * renderer appends it to the offer `href` as the ad param, carrying the
   * asset cross-site into the destination's attribution cookie.
   */
  mad: string
}

/** Our Offer DTO — decoupled from any card library's model. */
export interface AdOffer {
  id: string
  /** Como o SDK desenha esta oferta. `card` = oferta de catálogo. */
  creativeType: CreativeType
  courseName: string
  universityName: string
  logoUrl: string | null
  /** Destination PDP; the browser navigates to the mad-decorated variant. */
  href: string
  rating: { score: number; reviewsCount: number } | null
  price: {
    offeredPrice: number
    fullPrice: number | null
    discountPercentage: number | null
  } | null
  badges: { label: string }[]
  /** Sanitized markup for `html` creatives; null otherwise. */
  html: string | null
  /** Raw banner art for `image` creatives; null/absent otherwise (optional:
   * older servers don't send it). `link` creatives reuse courseName (label)
   * + href, like `text` does.
   *
   * `arts` is the general form — any number of breakpoints, each art carrying
   * its own minimum width. `src`/`srcDesktop` are the two-art legacy shape,
   * still accepted and equivalent to `[{minWidth: 0}, {minWidth: bp + 1}]`.
   * When both are present `arts` wins. */
  image?: {
    src: string
    srcDesktop: string | null
    alt: string
    arts?: AdImageArt[]
    /**
     * Como a arte ocupa a caixa, nos valores de `object-fit`. Declarado pelo
     * CRIATIVO e não pelo slot: dois anunciantes no mesmo placement precisam
     * poder divergir. Ausente mantém o default do CSS (`cover`).
     *
     * `scale-down` é o que resolve arte pequena borrada sem número mágico: ele
     * limita no tamanho natural da própria arte, ao contrário do `maxWidth`.
     */
    fit?: 'cover' | 'contain' | 'scale-down' | 'none' | 'fill'
    /** Trava a arte para não esticar além do tamanho natural e borrar. */
    maxWidth?: number
    /**
     * Raio do canto, em px. Ausente = o que o site definir em
     * `--mads-image-radius`, e o default do SDK é ZERO.
     *
     * Aqui e não no CSS do SDK pela mesma razão do `fit`: numa peça de imagem a
     * arte É o anúncio, e arredondar por conta própria muda o desenho de quem
     * anuncia. `0` é uma escolha válida — desliga o arredondamento do site.
     */
    radius?: number
    /** Global height of the image banner frame; intrinsic art.height is separate. */
    height?: number | string
    /** Maximum global image banner frame height. */
    maxHeight?: number | string
    /** Viewport width rules, sorted only by the renderer. */
    heightAt?: AdImageHeightRule[]
  } | null
  /**
   * Pack to mount for `component` creatives; null/absent otherwise (optional:
   * older servers don't send it). The renderer turns `name` into a CDN URL by
   * convention and hands `props` to the pack's `mount()`, so serving a new
   * component never requires an SDK change.
   */
  component?: { name: string, props: Record<string, unknown> } | null
  /**
   * TEMA resolvido desta oferta, quando ela pede um.
   *
   * Vem pronto do servidor: nome de tema do design system, tokens avulsos, ou os
   * dois. O SDK só repassa — quem sabe de quem é o rosto (o anunciante dono da
   * peça, ou o publisher do site) é a decisão, que tem o host e a marca em mãos.
   *
   * Só tem efeito em peça `component`: são os packs do Zilla que leem `--z-*` e
   * `data-theme`. Peça `image`/`link`/`html` é markup que o renderer monta aqui,
   * e a variável não encosta nele.
   */
  theme?: { preset: string | null, tokens: Record<string, string> } | null
  tracking: AdOfferTracking
}

export interface AdDecision {
  /** Joins impression ↔ click ↔ downstream of ONE decision (the funnel key). */
  id: string
  slotSlug: string
  /**
   * A unidade de entrega que ganhou: o AD SET. Opcional porque servidor antigo
   * não manda — nesse caso `campaignId` abaixo tem o mesmo valor.
   */
  adSetId?: string
  adSetName?: string
  /** @deprecated Sempre carregou o id do AD SET, nunca de campanha. Use
   * `adSetId`; para campanha de verdade, `AdOfferTracking.campaignId`. */
  campaignId: string
  /** @deprecated Ver `campaignId`. */
  campaignName: string
}

/** Gatilho de abertura de um popup. */
export const AD_POPUP_TRIGGERS = ['immediate', 'timeout', 'exit', 'idle', 'scroll'] as const
export type AdPopupTrigger = (typeof AD_POPUP_TRIGGERS)[number]

/**
 * Configuração de um slot POPUP: o slate dentro de um `<dialog>` modal.
 *
 * Vem da DECISÃO (servidor) e não do markup, porque o caso principal é o slot
 * auto-injetado (`mount`), onde ninguém escreve HTML — o `injectConfiguredSlots`
 * cria a div com o slug e mais nada. Aceita também vir por opção, para uso
 * imperativo e teste.
 */
export interface AdPopupConfig {
  /**
   * Quando a caixa abre:
   *
   * - `immediate`: no fim do render.
   * - `timeout`: depois de `delayMs` desde o render.
   * - `exit`: sinal de saída (cursor pela borda de cima no desktop, volta de aba
   *   escondida no mobile — não existe um sinal só para os dois).
   * - `idle`: `idleMs` sem NENHUMA interação. Mede silêncio, não tempo: quem
   *   está lendo e rolando nunca dispara, e é isso que o separa do `timeout`.
   * - `scroll`: quando a leitura passa de `scrollPercent` da página. É o sinal
   *   de engajamento — interrompe quem já se comprometeu com o texto, e não
   *   quem acabou de chegar.
   */
  trigger?: AdPopupTrigger
  /** Só com `trigger: 'timeout'`. Default 5000. */
  delayMs?: number
  /**
   * Só com `trigger: 'idle'`. Default 20000.
   *
   * Conta desde a ÚLTIMA interação, e qualquer uma reinicia: mover o mouse,
   * teclar, rolar, tocar. Aba escondida não conta como inatividade — quem
   * trocou de aba não está parado na página, está noutro lugar, e abrir para
   * uma tela que ninguém vê contaria impressão sem exibição.
   */
  idleMs?: number
  /**
   * Só com `trigger: 'scroll'`. Percentual de 1 a 100. Default 50.
   *
   * Medido sobre o que é ROLÁVEL, não sobre a altura do documento: numa página
   * que cabe inteira na tela não há rolagem possível, e exigir 50% de algo que
   * não existe deixaria o gatilho mudo para sempre. Aí ele conta como atingido
   * assim que o leitor chega ao fim do que dá para rolar.
   */
  scrollPercent?: number
  /**
   * Default `true`. Com `false` não há botão de fechar nem clique no backdrop —
   * o `Escape` do `<dialog>` continua funcionando e não deve ser removido: um
   * modal sem saída pelo teclado é armadilha de acessibilidade.
   */
  dismissible?: boolean
  /**
   * `true` mostra o modal UMA vez por sessão, por ad set.
   *
   * O cap do servidor conta ENTREGAS, e não alcança o que acontece dentro de
   * uma página — nem o leitor que abre seis artigos seguidos. É a diferença
   * entre um formato que interrompe uma vez e um que persegue.
   *
   * Cap de cliente é conselho, não garantia: quem limpa o storage vê de novo.
   * Vale mesmo assim, porque o caso que ele resolve não é fraude, é o leitor
   * comum navegando.
   */
  oncePerSession?: boolean
  /**
   * A largura da caixa, JÁ RESOLVIDA pelo servidor — em px, ou a tela inteira.
   *
   * O SDK não tem default para cair: ele RECEBE e aplica. O número era
   * `min(92vw, 640px)` escrito no CSS daqui, longe de quem decidia a peça, e a
   * prévia do admin tinha de copiá-lo para não mentir — dois lugares que
   * divergiam calados no dia em que um mudasse.
   *
   * `'full'` não é um número grande: é outra apresentação. A caixa toma a tela
   * toda, perde o arredondamento e o backdrop deixa de aparecer.
   *
   * Opcional só por causa de servidor antigo, que não manda. Sem valor o SDK
   * não inventa um: a caixa fica do tamanho do conteúdo, limitada pela altura.
   */
  maxWidth?: number | 'full'
  /**
   * Quanto da TELA a caixa pode tomar, em porcentagem. Também já resolvido.
   *
   * Existe para sobrar borda em tela estreita: sem ela o botão de fechar, que
   * mora no canto de fora, sai da viewport e fica inalcançável. Quanta borda é
   * decisão de desenho, e por isso deixou de ser constante daqui.
   */
  viewportPercent?: number
  /** Rótulo acessível da caixa. Default "Publicidade". */
  label?: string
  closeLabel?: string
}

export interface AdsResponse {
  decision: AdDecision
  /** Resolved slot contract the renderer honours. */
  unit: AdFormat
  /** IAB size of the reserved box (`dynamic` = sem medida fixa). */
  size: AdSize
  /** How the SDK should render this fill (render | swap | custom). */
  renderMode: RenderMode
  theme: string | null
  /** The slot's config-driven placement (informational echo); absent/null when
   * the host owns placement. */
  mount?: AdMount | null
  offers: AdOffer[]
  /** Presente quando o slot é POPUP — traz o gatilho e o comportamento da caixa. */
  popup?: AdPopupConfig | null
  generatedAt: string
  /**
   * A decisão levou o VISITANTE em conta (freq cap, seen-set, ranking por
   * pessoa). `false` na decisão anônima — a do SSR, por construção. Opcional:
   * servidor antigo não manda.
   */
  personalized?: boolean
  /**
   * Presente SÓ quando a veiculação servida está em `preview`: o que está na
   * tela NÃO está no ar para o visitante.
   *
   * Vem do estado da unidade vencedora, e não do modo da requisição — com o
   * modo ligado, uma veiculação `active` pode vencer a disputa normalmente.
   * Marcar essa como preview diria que uma peça de produção não é de produção.
   */
  preview?: AdPreviewInfo
}

/** O relato de preview que acompanha a decisão. Ver `AdsResponse.preview`. */
export interface AdPreviewInfo {
  /**
   * A unidade foi FORÇADA: furou targeting e teto de frequência para chegar
   * aqui. É o caso em que a peça não apareceria nesta página nem depois de
   * publicada — e por isso a moldura diz isso com todas as letras.
   */
  forced: boolean
}

/** No-fill body — the renderer collapses the slot silently. */
export interface AdsNoFill {
  decision: null
  reason: 'no-slot' | 'no-eligible-campaign' | 'no-offers' | 'ads-disabled'
}

/** One `/ads/slots` bootstrap entry: a slot the SDK auto-injects on load. */
export interface AdSlotBinding {
  slug: string
  size: AdSize
  mount: AdMount
}

/** `/ads/slots` response — the client's injectable slots. */
export interface AdSlotBindings {
  slots: AdSlotBinding[]
}

export type AdsResult = AdsResponse | AdsNoFill

/** Runtime context the SDK sends alongside the slot slug. */
export interface AdRenderContext {
  pageType?: string
  query?: string
  /** Host-declared page-context tags (e.g. `['undergraduate']`) for targeting.
   * Matches the "same URL, different tab/section" case. Declarative via
   * `data-montilla-tags` or runtime via `montilla.ads.setContext({ tags })`. */
  tags?: string[]
  /**
   * Padrão de rota da página (`/cursos/:slug`) — o MESMO valor que vai em
   * `route_pattern` no page view. Mira "a página de curso" como ENTIDADE, em vez
   * de `url contém /cursos/`, que também pegaria `/blog/cursos/como-escolher`.
   *
   * Quem sabe a rota é o roteador do host, então só o host manda: o servidor não
   * deriva padrão da URL (derivar inventaria rotas que não existem lá). O módulo
   * Nuxt preenche isto sozinho; fora dele, `montilla.ads.setContext({ routePattern })`.
   */
  routePattern?: string
  /**
   * Valores por parâmetro de pool: `{ curso: 'enfermagem', nivel: 'Graduação' }`.
   *
   * Estreitam o RECALL da pool que servir este espaço, pelos buracos que ela
   * declarou. Nome que a pool não conhece é ignorado, e volta em `varsIgnored`.
   *
   * Não confundir com `tags`: elas decidem qual veiculação ganha o espaço; estes
   * decidem o que a pool dela devolve. Declarativo via `data-montilla-vars`, ou
   * em runtime por `montilla.ads.render(el, { vars })`.
   */
  vars?: Record<string, string>
  device?: string
  /** Extra geo hint (`lat,lon`); resolved automatically when omitted. */
  geo?: string
  /** Overrides the slot-configured slate size. */
  limit?: number
}

/** Options for `montilla.ads.render(target, options)`. */
export interface AdRenderOptions extends AdRenderContext {
  /** Logical slot slug registered in the admin (join key). */
  slot: string
  /**
   * IAB size to reserve for the slot BEFORE the decision arrives — mirrors the
   * slot's configured size in the admin. Drives the anti-CLS box: the SDK
   * reserves the format's intrinsic height (and caps the width) synchronously so
   * the fill never shifts the page. Omit (or `'dynamic'`) for responsive/full-width
   * slots — pair `dynamic` with `height` or `ratio` below to keep them CLS-safe.
   */
  size?: AdSize
  /**
   * The width the art OCCUPIES in this slot, as a `sizes` value
   * (`(min-width: 1200px) 320px, 77vw`). Declarative via `data-montilla-sizes`.
   *
   * Only the host knows its layout, so this wins over every guess the SDK makes:
   * without it the SDK uses the slot size (IAB/custom), or — for a `dynamic`
   * slot — each art's own width, capped at the viewport. It only matters for art
   * that has resolution variants (`srcset`).
   */
  sizes?: string
  /**
   * Full-width / responsive reservation: the height to reserve for a `dynamic`
   * slot while its width fills the container (no cap). A bare number is treated
   * as px (`120` → `120px`); any CSS length passes through (`8rem`). Use this for
   * a full-bleed top masthead. Ignored when a fixed `size` is set.
   */
  height?: string | number
  /**
   * Alternative to `height` for a `dynamic` slot: reserve the height via an
   * `aspect-ratio` (e.g. `'6 / 1'`) that scales with the full container width.
   * Ignored when `height` or a fixed `size` is set.
   */
  ratio?: string
  /**
   * What the reserved box does on a no-fill. `true` (default) collapses it to
   * zero height so an empty slot leaves no gap; `false` keeps the reserved box
   * (zero shift in every case, at the cost of empty space). Falls back to the
   * client-wide `ads.collapseEmpty` config when omitted.
   */
  collapse?: boolean
  /**
   * For `render` mode: mount inside a Shadow DOM (default, full style
   * isolation) or, when `false`, in the host's light DOM so the page's own
   * CSS can style the `.mads` slate. Ignored by `swap`/`custom`.
   */
  shadow?: boolean
  /**
   * Advertising label above the slate — OPT-IN, no label by default. `true` uses
   * `'Publicidade'`; a string is the text (`'Anúncio'`, `'Patrocinado'`).
   *
   * Off by default because this inventory is mostly house advertising on formats
   * that already read as ads. Paid third-party placement is the case that needs
   * it — an offer from a partner university, a native card inside an article —
   * and there it must be asked for, per slot or via the client default.
   * Disclosure rules (CDC art. 36, CONAR) bind the publisher, not this default.
   */
  adLabel?: boolean | string
  /**
   * Props do criativo `component` que só o HOST sabe, declaradas no slot como
   * JSON em `data-montilla-props`.
   *
   * Existe porque as props de um criativo são ESTÁTICAS: servem para toda a
   * campanha. Um valor que varia por página — o slug do artigo em que o slot
   * está, por exemplo — não pode vir do admin, e sem isto o pack não teria como
   * recebê-lo. É o que preserva num criativo `component` o mesmo atributo de
   * evento que um componente importado direto já recebia por prop.
   *
   * Precedência: o CRIATIVO VENCE, com merge profundo. O host preenche o que
   * falta, não redefine o que o operador configurou.
   *
   * ```html
   * <div data-montilla-slot="revista-article-body-10"
   *      data-montilla-props='{"tracking":{"payload":{"article_slug":"x"}}}'></div>
   * ```
   *
   * Ignorado nos outros tipos de criativo — só `component` tem props.
   */
  extraProps?: Record<string, unknown>
  /**
   * Força o modo popup para este render, ignorando o que a decisão trouxer. Para
   * uso imperativo e teste; em produção quem manda é o servidor.
   */
  popup?: AdPopupConfig
  /**
   * Injection mode: treat `target` as an ANCHOR (not the container) — the SDK
   * creates a fresh slot element at this position relative to it and renders
   * there. Lets you place an ad on markup you can't edit ("after the 3rd
   * paragraph", "inside .promo-rail") — pair with an XPath/CSS `target`.
   * Unmount removes the injected element entirely.
   * @example montilla.ads.render('//article/p[3]', { slot: 'inline', mountPosition: 'after' })
   */
  mountPosition?: AdMountPosition
  /**
   * Decisão JÁ TOMADA (pelo servidor). Presente → o render **NÃO** chama `/ads`.
   *
   * É a metade de cliente do SSR de slot: o Nitro decide durante o render da
   * página, a decisão viaja no payload, e aqui ela é só pintada. O contrato
   * proíbe uma segunda decisão — ela chegaria depois, poderia divergir da que
   * gerou os preloads do `<head>`, e contaria a entrega duas vezes.
   *
   * Todo o resto é o caminho de sempre: reserva de caixa, pack, popup,
   * `trackSlate`, debug. A IMPRESSÃO continua sendo do cliente — anúncio pintado
   * no servidor não foi visto.
   */
  decision?: AdsResponse
  /**
   * Hidratar o slate que o SERVIDOR já pintou, em vez de pintar de novo.
   *
   * Só faz sentido com `decision`: o markup veio no HTML (dentro de um
   * `<template shadowrootmode>`), e o que falta é anexar tracking, montar os
   * packs e devolver o handle. Ver `renderSlateToString` no subpath `ads-ssr`.
   *
   * Rede de segurança: se o markup não bater com as ofertas da decisão, o render
   * completo acontece normalmente. Um SSR desalinhado não pode virar página
   * quebrada — o pior caso tem de ser o slot de hoje.
   */
  hydrate?: boolean
}

/** Handle returned by `render` — lets the host unmount/inspect the slot. */
export interface AdSlotHandle {
  slot: string
  /** Null while loading and on no-fill. */
  decision: AdDecision | null
  /** How this fill was rendered. */
  renderMode?: RenderMode
  /**
   * De onde veio a decisão deste slot. Ausente = o SDK decidiu no cliente.
   *
   * - `'decision'` — o servidor decidiu, o cliente pintou.
   * - `'markup'` — o servidor decidiu **e** pintou; o cliente hidratou.
   */
  ssr?: 'decision' | 'markup'
  /** Served offers — populated for `custom` (headless) so the host app can
   * build its own UI from the decision data. */
  offers?: AdOffer[]
  unmount: () => void
}

/** visitorId travels in a header (never in the URL). */
export const MONTILLA_VISITOR_HEADER = 'x-montilla-visitor'
/**
 * O preview de veiculação vindo do browser, em sintaxe de cookie
 * (`montilla_preview=1; montilla_preview_force=<adSetId>`). O cookie do domínio
 * da página não chega ao rum-api numa chamada cross-origin, então o SDK o
 * repassa aqui. Ver `cms/preview.ts`.
 */
export const MONTILLA_PREVIEW_HEADER = 'x-montilla-preview'

/**
 * Uma oferta servida, como `montilla.ads.inspect()` a relata: os ids de
 * rastreio e o bastante para alguém reconhecer a peça — sem a arte nem o HTML.
 */
export interface AdSlotInspectionOffer {
  offerId: string
  adId: string | null
  /** Campanha DE VERDADE (a do item). `null` quando o servidor é antigo. */
  campaignId: string | null
  position: number
  mad: string
  creativeType: CreativeType
  /** Pack (`component.name`), `alt` da arte ou o curso do card — o que houver. */
  label: string | null
}

/** O que `montilla.ads.inspect()` sabe de um slot `[data-montilla-slot]`. */
export interface AdSlotInspection {
  element: HTMLElement
  slot: string
  /**
   * Onde a decisão foi tomada: `server` = veio pronta (SSR), `client` = o SDK
   * foi ao `/ads`. `null` = o SDK ainda não decidiu este slot, ou o debug de
   * área (`box`) o pintou sem perguntar nada.
   */
  source: 'server' | 'client' | null
  /** Quando a decisão chegou ao SDK, em ms desde a época. */
  decidedAt: number | null
  /** A peça ainda está montada no elemento. */
  mounted: boolean
  /** A publicação vencedora, ou `null` no vazio e antes de decidir. */
  decision: AdDecision | null
  /** Por que veio vazio. `transport` = o `/ads` não respondeu. */
  reason: AdsNoFill['reason'] | 'transport' | null
  renderMode: RenderMode | null
  /** Presente quando a peça servida está em preview. Ver `AdsResponse.preview`. */
  preview: AdPreviewInfo | null
  personalized: boolean | null
  offers: AdSlotInspectionOffer[]
}

/** Um candidato do dry-run: cada publicação que podia servir o slot, e por que serviu ou não. */
export interface AdsDryRunCandidate {
  /** @deprecated Nome de fio legado: o valor é o id do AD SET. */
  campaignId: string
  /** @deprecated Idem: nome do AD SET. */
  campaignName: string
  eligible: boolean
  /** A razão do motor: regra que casou ou falhou, voo, teto, disputa. */
  reason: string
  priority: number
  weight: number
  /** Status do ad set. Ausente em servidor antigo. */
  status?: string
}

/** O que `montilla.ads.explain()` devolve: a decisão do dry-run e a disputa inteira. */
export interface AdsExplanation {
  slot: string
  /** De onde veio a decisão que está NA TELA — o dry-run pergunta como ela perguntou. */
  source: 'server' | 'client' | null
  result: AdsResult
  candidates: AdsDryRunCandidate[]
}

export interface AdExplainOptions {
  /**
   * Como pedir o preview no dry-run. Ausente = como a página está (os cookies);
   * `false` = sem preview; `true` = com preview e sem forçar; objeto = com
   * preview forçando esse ad set. Os dois últimos existem para listar o que dá
   * para forçar antes de ligar qualquer coisa.
   */
  preview?: boolean | { forcedAdSetId: string | null }
}
/** Public decision endpoint on the rum-api host. */
export const ADS_PATH = '/ads'

/** Bootstrap endpoint listing the client's injectable slots (config-driven placement). */
export const ADS_SLOTS_PATH = '/ads/slots'
